import { resolve, singleton } from 'aurelia';
import { HttpClient } from './http-client';

// One row of the admin scraper report as returned by GET /api/admin/scraper-report.
// lastUpdatedAt is the last time the source wrote any row (its last successful scrape);
// lastFirstSeenAt is the last time it found a brand-new release. Both are null when the
// source has never stored anything.
export interface ScraperReportRow {
  source: string;
  enabled: boolean;
  lastUpdatedAt: string | null;
  lastFirstSeenAt: string | null;
  releaseCount: number;
}

// How a row is presented: healthy, worth a look, or broken. Disabled sources are called out
// separately so an intentionally-off source is never shown as a failure.
export type ScraperHealth = 'ok' | 'warn' | 'stale' | 'never' | 'disabled';

// A source that has not written anything in this long is suspicious. Scrapes run well within
// a day, so a day and a half of silence means the last run produced nothing at all.
const WARN_AFTER_HOURS = 36;
const STALE_AFTER_HOURS = 72;

// Owns the admin scraper-report panel: loads each registered source's last scrape activity so an
// admin can spot a scraper that has silently stopped working. Mirrors the other singleton admin
// services — shared state lives here and the panel binds to it directly.
@singleton()
export class ScraperReportAdminService {
  private readonly http: HttpClient = resolve(HttpClient);

  panelOpen: boolean = false;
  loading: boolean = false;
  error: string | null = null;
  rows: ScraperReportRow[] = [];

  // Loads the report. Safe to call whenever the panel is opened; failures leave a readable
  // error and an empty list.
  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const response = await this.http.get('/api/admin/scraper-report');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      this.rows = await response.json();
    } catch (e: any) {
      this.error = `Could not load the scraper report: ${e.message}`;
      this.rows = [];
    } finally {
      this.loading = false;
    }
  }

  // Hours since the given UTC timestamp, or null when it is absent/unparseable.
  private hoursSince(iso: string | null): number | null {
    if (!iso) return null;
    // Timestamps are stored as UTC but serialized without an offset, so append 'Z' when the
    // payload carries no timezone designator, otherwise the browser reads them as local time.
    const normalized = /[Zz]|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`;
    const then = Date.parse(normalized);
    if (Number.isNaN(then)) return null;
    return (Date.now() - then) / 36e5;
  }

  health(row: ScraperReportRow): ScraperHealth {
    if (!row.enabled) return 'disabled';
    const hours = this.hoursSince(row.lastUpdatedAt);
    if (hours === null) return 'never';
    if (hours >= STALE_AFTER_HOURS) return 'stale';
    if (hours >= WARN_AFTER_HOURS) return 'warn';
    return 'ok';
  }

  // Compact "how long ago" label for a timestamp column.
  relative(iso: string | null): string {
    const hours = this.hoursSince(iso);
    if (hours === null) return 'never';
    if (hours < 1) return 'just now';
    if (hours < 24) return `${Math.floor(hours)}h ago`;
    const days = Math.floor(hours / 24);
    return days === 1 ? '1 day ago' : `${days} days ago`;
  }

  // Number of enabled sources that are not currently healthy — the headline number an admin
  // actually cares about when opening the panel.
  get problemCount(): number {
    return this.rows.filter(r => {
      const h = this.health(r);
      return h === 'warn' || h === 'stale' || h === 'never';
    }).length;
  }

  async openPanel(): Promise<void> {
    this.panelOpen = true;
    await this.load();
  }

  closePanel(): void {
    this.panelOpen = false;
    this.error = null;
  }
}
